import {
  CheckTypeEnum,
  TemplateRuleCheckModel,
  TemplateRuleCheckTargetEnum,
  TemplateRuleCheckVariantModel,
  TemplateRuleTypeEnum,
} from '../../../shared/models';
import { JsonLogic } from '../../../utils';

export interface DefaultTemplateRuleCheckVariant {
  label: string;
  position: number;
  activationJsonLogic: JsonLogic;
  checkSettings: TemplateRuleCheckVariantModel['checkSettings'];
}

export interface DefaultTemplateRule {
  type: 'check' | 'control';
  activationJsonLogic?: JsonLogic | null;

  // For type 'check'
  checkType?: CheckTypeEnum;
  checkSubtype?: string;
  checkSettings?: TemplateRuleCheckModel['checkSettings'];
  checkTarget?: TemplateRuleCheckTargetEnum;
  deduplication?: boolean;
  variants?: DefaultTemplateRuleCheckVariant[];

  // For type 'control'
  controlKey?: string;
}

export interface DefaultTemplate {
  name: string;
  description?: string;
  isDefault?: boolean;
  caseAutoApproval?: boolean;
  autoRejectProhibitedRisk?: boolean;
  caseReviewApprovalEnforceAllControlsCompleted?: boolean;
  mergeIdentityChecks?: boolean;
  caseAutoReopening?: boolean;
  rules: DefaultTemplateRule[];
}

export const DEFAULT_TEMPLATES: DefaultTemplate[] = [
  {
    name: 'Simple KYB Template',
    isDefault: true,
    caseAutoApproval: true,
    autoRejectProhibitedRisk: true,
    caseReviewApprovalEnforceAllControlsCompleted: false,
    mergeIdentityChecks: true,
    caseAutoReopening: true,
    rules: [
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.aml,
        checkTarget: TemplateRuleCheckTargetEnum.individual_any,
        activationJsonLogic: {
          or: [
            { '==': [{ var: 'individual.is_beneficial_owner' }, true] },
            {
              arrayContains: [
                ['legal_representative'],
                { var: 'individual.roles' },
              ],
            },
            { '==': [{ var: 'individual.is_business_contact' }, true] },
          ],
        },
        deduplication: false,
        variants: [
          {
            label: 'Lower fuzziness on legal reps',
            position: 1,
            activationJsonLogic: {
              and: [
                {
                  arrayContains: [
                    ['legal_representative'],
                    { var: 'individual.roles' },
                  ],
                },
              ],
            },
            checkSettings: { fuzziness: 0.2 },
          },
        ],
      },
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.id_document,
        checkTarget: TemplateRuleCheckTargetEnum.individual_any,
        activationJsonLogic: {
          or: [{ '==': [{ var: 'individual.is_beneficial_owner' }, true] }],
        },
        deduplication: false,
      },
      {
        type: TemplateRuleTypeEnum.control,
        controlKey: 'company_older_than_2_years',
      },
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.company_monitoring,
        checkTarget: TemplateRuleCheckTargetEnum.company_main,
        deduplication: false,
      },
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.document,
        checkSubtype: 'document_type:registration_certificate',
        checkTarget: TemplateRuleCheckTargetEnum.company_main,
        deduplication: false,
        checkSettings: {
          documentTypeKey: 'registration_certificate',
        },
      },
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.aml,
        checkTarget: TemplateRuleCheckTargetEnum.company_main,
        deduplication: false,
      },
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.id_verification,
        checkTarget: TemplateRuleCheckTargetEnum.individual_any,
        activationJsonLogic: {
          or: [{ '==': [{ var: 'individual.is_business_contact' }, true] }],
        },
        deduplication: false,
      },
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.online_reputation,
        checkTarget: TemplateRuleCheckTargetEnum.company_main,
        deduplication: false,
      },
      {
        type: TemplateRuleTypeEnum.control,
        controlKey: 'risk_is_set',
      },
      {
        type: TemplateRuleTypeEnum.control,
        controlKey: 'at_least_one_ubo_identified',
      },
      {
        type: TemplateRuleTypeEnum.control,
        controlKey: 'manual_kyb_review',
        activationJsonLogic: {
          and: [{ '==': [{ var: 'case.risk.level' }, 'high'] }],
        },
      },
    ],
  },
  {
    name: 'Simple KYC Template',
    isDefault: false,
    caseAutoApproval: false,
    autoRejectProhibitedRisk: true,
    caseReviewApprovalEnforceAllControlsCompleted: false,
    mergeIdentityChecks: false,
    caseAutoReopening: true,
    rules: [
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.id_verification,
        checkTarget: TemplateRuleCheckTargetEnum.individual_any,
        deduplication: false,
      },
      {
        type: TemplateRuleTypeEnum.check,
        checkType: CheckTypeEnum.aml,
        checkTarget: TemplateRuleCheckTargetEnum.individual_any,
        deduplication: false,
      },
    ],
  },
];
